/**
 * Model - Base class for data entities with caching
 *
 * Foundation for data models that fetch and cache server responses. Provides
 * consistent caching pattern for entity data (users, events, groups, etc.).
 *
 * PATTERN: Extend Model for each entity type that needs server data caching.
 *
 * KEY FEATURES:
 * - Automatic response caching (by type and key)
 * - Force refresh option
 * - Cache invalidation
 * - Promise-based API
 * - Shared cache across instances
 *
 * @class Model
 * @extends Class
 *
 * @param {Object} [setup] - Configuration
 *
 * @example
 * // Extend Model for entity type
 * const User = Model.inherit({
 *     namespace: 'User',
 *     singleton: true,
 *     api: {
 *         get: {
 *             public: true,
 *             value: function(context, options) {
 *                 return this.cache(options, function() {
 *                     return this.XHR.get('/api/user');
 *                 });
 *             }
 *         }
 *     }
 * });
 *
 * @example
 * // Use cached data
 * User.get({ key: userId, type: 'user' }).then(function(userData) {
 *     // Cached if previously fetched
 * });
 */
define("Model", [`Class`, `module`], function (Super, Self) {
	const cache = [];

	return Super.inherit({
		namespace: Self.id,
		requires: [`App`, `System`, `XHR`],
		constructor(setup) {
			this.subscriptions = [];
		},
		api: {
			/**
			 * Add a caching layer, so future requests for the same result can use the saved value.
			 * @method Model#cache
			 * @param {Object} options
			 * @param {boolean} options.force=false - If true, ignore any cached value
			 * @param {string} options.key
			 * @param {string} options.type
			 * @param {function} obtain
			 */
			cache: {
				value(options, obtain) {
					let cached;
					let value;

					if (options.force !== true) {
						cached = this.find(cache, {
							owner: this,
							type: options.type,
							key: options.key,
						});

						if (this.isObject(cached)) {
							value = cached.value;
						}
					}

					if (this.isUndefined(value)) {
						value = obtain.call(this);

						this.extract(cache, {
							owner: this,
							type: options.type,
							key: options.key,
						});

						cache.push({
							owner: this,
							type: options.type,
							key: options.key,
							value: this.isPromise(value) ? value : this.clone(value),
						});
					}

					if (this.isPromise(value)) {
						// If the cached item is a Promise, create a clone of the resolved value for the consumer
						return this.when({
							promise: value,
							then: function (val) {
								return this.clone(val);
							},
						});
					} else {
						return value;
					}
				},
			},

			/**
			 * Used for invalidating entries in the cache.
			 * @method Model#flushCache
			 * @param {Object} properties
			 * @param {Class} properties.owner
			 * @param {string} properties.key
			 * @param {string} properties.type
			 */
			flushCache: {
				value(properties) {
					// Remove any cached entries that match the passed set of properties
					this.extract(cache, properties);
				},
			},
		},
	});
});

//# sourceMappingURL=Model.js.map